import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider } from './contexts/AuthContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ToastProvider } from './contexts/ToastContext';

import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import SeoManager from './components/SeoManager';

import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import AgendaPage from './pages/AgendaPage';
import InventoryPage from './pages/InventoryPage';
import BillingPage from './pages/BillingPage';
import PatientRecordPage from './pages/PatientRecordPage';
import PaymentPage from './pages/PaymentPage';
import SubscriptionGuard from './components/SubscriptionGuard';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AnamnesePage from './pages/AnamnesePage';
import AIAnalysisPage from './pages/AIAnalysisPage';
import ImagesPage from './pages/ImagesPage';
import ProntuariosPage from './pages/ProntuariosPage';
import PrescriptionsPage from './pages/PrescriptionsPage';
import ContractsPage from './pages/ContractsPage';
import RemindersPage from './pages/RemindersPage';
import CompliancePage from './pages/CompliancePage';

import AlignersPage from './pages/AlignersPage';

import WhatsAppBusinessPage from './pages/WhatsAppBusinessPage';
import WhatsAppAdminPage from './pages/WhatsAppAdminPage';

import PublicAnamnesePage from './pages/PublicAnamnesePage';
import PublicAnamnesePageTest from './pages/PublicAnamnesePageTest';
import PublicSignPage from './pages/PublicSignPage';
import PublicSignDonePage from './pages/PublicSignDonePage';
import PublicValidateDocumentPage from './pages/PublicValidateDocumentPage';

import PublicTreatmentAcceptancePage from './pages/PublicTreatmentAcceptancePage';
import PublicEvolutionAcknowledgementPage from './pages/PublicEvolutionAcknowledgementPage';

import PublicOnlineBookingPage from './pages/PublicOnlineBookingPage';

import PublicCancelOnlineBookingPage from './pages/PublicCancelOnlineBookingPage';

import PublicRescheduleOnlineBookingPage from './pages/PublicRescheduleOnlineBookingPage';

import PublicPatientPhotoPage from './pages/PublicPatientPhotoPage';

import Layout from './components/Layout';
import MarketingHome from './pages/MarketingHome';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LgpdPage from './pages/LgpdPage';
import SecurityPage from './pages/SecurityPage';
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ActivateAccountPage from './pages/ActivateAccountPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ContactPage from './pages/ContactPage';
import SystemStatusPage from './pages/SystemStatusPage';
import GettingStartedPage from './pages/GettingStartedPage';
import TutorialsPage from './pages/TutorialsPage';
import OdontogramaTutorialPage from './pages/OdontogramaTutorialPage';
import DocumentosTutorialPage from './pages/DocumentosTutorialPage';
import AnamneseTutorialPage from './pages/AnamneseTutorialPage';
import ConfiguracoesTutorialPage from './pages/ConfiguracoesTutorialPage';
import AgendaTutorialPage from './pages/AgendaTutorialPage';
import ImagesTutorialPage from './pages/ImagesTutorialPage';
import AIAnalysisTutorialPage from './pages/AIAnalysisTutorialPage';
import BillingTutorialPage from './pages/BillingTutorialPage';
import DashboardTutorialPage from './pages/DashboardTutorialPage';
import InventoryTutorialPage from './pages/InventoryTutorialPage';
import ProntuariosTutorialPage from './pages/ProntuariosTutorialPage';
import AlignersTutorialPage from './pages/AlignersTutorialPage';
import RemindersTutorialPage from './pages/RemindersTutorialPage';
import BenefitProgramTutorialPage from './pages/BenefitProgramTutorialPage';
import WhatsAppExtensionTutorialPage from "./pages/WhatsAppExtensionTutorialPage";
import InstallAppPage from './pages/InstallAppPage';
import ClinicUsersPage from './pages/ClinicUsersPage';
import AuditPage from './pages/AuditPage';
import AccountSecurityPage from './pages/AccountSecurityPage';
import ClinicInvitationPage from './pages/ClinicInvitationPage';
import CreateOwnClinicPage from './pages/CreateOwnClinicPage';
import WhatsAppBusinessTutorialPage from './pages/WhatsAppBusinessTutorialPage';
import ClinicUsersTutorialPage from './pages/ClinicUsersTutorialPage';
import AccountSecurityAuditTutorialPage from './pages/AccountSecurityAuditTutorialPage';
import ComplianceTutorialPage from './pages/ComplianceTutorialPage';
import PublicComplianceAssessmentPage from './pages/PublicComplianceAssessmentPage';
import './App.css';

function App() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  useEffect(() => {
    const handleNewVersion = () => {
      setNewVersionAvailable(true);
    };

    window.addEventListener(
      'odontoassist:new-version-available',
      handleNewVersion
    );

    return () => {
      window.removeEventListener(
        'odontoassist:new-version-available',
        handleNewVersion
      );
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <OnboardingProvider>
        <ToastProvider>
          <Router>
            <SeoManager />

            <div className="App">

              {newVersionAvailable && (
                <div
                  style={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 99999,
                    background: '#2563eb',
                    color: '#fff',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    maxWidth: '320px',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Nova versão disponível
                  </div>

                  <div style={{ fontSize: '13px', marginBottom: 10 }}>
                    O OdontoAssist foi atualizado. Clique abaixo para carregar a versão mais recente.
                  </div>

                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      background: '#fff',
                      color: '#2563eb',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Atualizar agora
                  </button>
                </div>
              )}

              <Routes>
                {/* --- Rotas públicas --- */}
                <Route path="/" element={<MarketingHome />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/instalar" element={<InstallAppPage />} />
                <Route path="/privacidade" element={<PrivacyPage />} />
                <Route path="/termos-de-uso" element={<TermsPage />} />
                <Route path="/lgpd" element={<LgpdPage />} />
                <Route path="/seguranca" element={<SecurityPage />} />
                <Route path="/ajuda" element={<HelpCenterPage />} />
                <Route path="/ajuda/primeiros-passos" element={<GettingStartedPage />} />
                <Route path="/ajuda/tutoriais" element={<TutorialsPage />} />
                <Route path="/ajuda/tutoriais/odontograma" element={<OdontogramaTutorialPage />} />
                <Route path="/ajuda/tutoriais/documentos" element={<DocumentosTutorialPage />} />
                <Route path="/ajuda/tutoriais/anamnese" element={<AnamneseTutorialPage />} />
                <Route path="/ajuda/tutoriais/configuracoes" element={<ConfiguracoesTutorialPage />} />
                <Route path="/ajuda/tutoriais/agendamento" element={<AgendaTutorialPage />} />
                <Route path="/ajuda/tutoriais/usuarios-permissoes" element={<ClinicUsersTutorialPage />} />
                <Route path="/ajuda/tutoriais/seguranca-auditoria" element={<AccountSecurityAuditTutorialPage />} />
                <Route path="/ajuda/tutoriais/boas-praticas" element={<ComplianceTutorialPage />} />
                <Route path="/ajuda/tutoriais/lembretes" element={<RemindersTutorialPage />} />
                <Route path="/ajuda/tutoriais/analise-ia" element={<AIAnalysisTutorialPage />} />
                <Route path="/ajuda/tutoriais/dashboard" element={<DashboardTutorialPage />} />
                <Route path="/ajuda/tutoriais/financeiro" element={<BillingTutorialPage />} />
                <Route path="/ajuda/tutoriais/cobranca-pagamentos" element={<BillingTutorialPage />} />
                <Route path="/ajuda/tutoriais/estoque" element={<InventoryTutorialPage />} />
                <Route path="/ajuda/tutoriais/prontuarios" element={<ProntuariosTutorialPage />} />
                <Route path="/ajuda/tutoriais/extensao-whatsapp" element={<WhatsAppExtensionTutorialPage />} />
                <Route
                  path="/ajuda/tutoriais/imagens"
                  element={<ImagesTutorialPage />}
                />  
                <Route
                  path="/ajuda/tutoriais/alinhadores"
                  element={<AlignersTutorialPage />}
                />           
                <Route path="/ajuda/tutoriais/programa-beneficios" element={<BenefitProgramTutorialPage />} />
                <Route path="/contato" element={<ContactPage />} />
                <Route path="/status" element={<SystemStatusPage />} />
                <Route path="/diagnostico-clinica-2026" element={<PublicComplianceAssessmentPage />} />
                <Route path="/anamnese/publico/:publicLinkId" element={<PublicAnamnesePage />} />
                <Route path="/anamnese/teste/:publicLinkId" element={<PublicAnamnesePageTest />} />
                <Route path="/public/sign/:token" element={<PublicSignPage />} />
                <Route path="/public/sign/done" element={<PublicSignDonePage />} />
                <Route path="/public/validar-documento/:code" element={<PublicValidateDocumentPage />} />
                <Route path="/public/evolucao-ciencia/:token" element={<PublicEvolutionAcknowledgementPage />} />
                <Route path="/public/autoagendamento/:token" element={<PublicOnlineBookingPage />} />
                <Route
                  path="/public/autoagendamento/cancelar/:cancelToken"
                  element={<PublicCancelOnlineBookingPage />}
                /> 
                <Route
                  path="/public/autoagendamento/remarcar/:rescheduleToken"
                  element={<PublicRescheduleOnlineBookingPage />}
                />                               
                <Route path="/public/patient-photo/:token" element={<PublicPatientPhotoPage />} />
                <Route path="/public/plano-tratamento-aceite/:token" element={<PublicTreatmentAcceptancePage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/ativar-conta/:token" element={<ActivateAccountPage />} />
                <Route path="/convite-clinica/:token" element={<ClinicInvitationPage />} />
                {/* --- Rotas administrativas --- */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/whatsapp"
                  element={
                    <AdminRoute>
                      <WhatsAppAdminPage />
                    </AdminRoute>
                  }
                />

                {/* --- Rotas autenticadas --- */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute permission="dashboard.view">
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/prontuarios"
                  element={
                    <ProtectedRoute permission="patients.view">
                      <ProntuariosPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/agenda"
                  element={
                    <ProtectedRoute permission="appointments.view">
                      <AgendaPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/whatsapp-business"
                  element={
                    <ProtectedRoute permission="whatsapp.view">
                      <WhatsAppBusinessPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/lembretes"
                  element={
                    <ProtectedRoute permission="appointments.view">
                      <RemindersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/alinhadores"
                  element={
                    <ProtectedRoute permission="aligners.view">
                      <AlignersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/estoque"
                  element={
                    <ProtectedRoute permission="inventory.view">
                      <InventoryPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/financeiro"
                  element={
                    <ProtectedRoute permission="billing.view">
                      <BillingPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/cobranca"
                  element={<Navigate to="/financeiro" replace />}
                />

                <Route
                  path="/pacientes/:patientId/prontuario"
                  element={
                    <ProtectedRoute permission="clinical_records.view">
                      <PatientRecordPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pagamento/:subscriptionId"
                  element={
                    <ProtectedRoute permission="subscription.view">
                      <PaymentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/configuracoes"
                  element={
                    <ProtectedRoute permission="settings.update_clinic">
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/usuarios"
                  element={
                    <ProtectedRoute permission="settings.manage_users">
                      <ClinicUsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/auditoria"
                  element={
                    <ProtectedRoute permission="audit.view">
                      <AuditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/seguranca-da-conta"
                  element={
                    <ProtectedRoute skipSubscription>
                      <AccountSecurityPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/criar-minha-clinica"
                  element={
                    <ProtectedRoute skipSubscription>
                      <CreateOwnClinicPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/assinatura"
                  element={
                    <ProtectedRoute permission="subscription.view">
                      <SubscriptionPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/anamnese"
                  element={
                    <ProtectedRoute permission="anamnese.view">
                      <AnamnesePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ia"
                  element={
                    <ProtectedRoute permission="ai.clinical_use">
                      <AIAnalysisPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/imagens"
                  element={
                    <ProtectedRoute permission="images.view">
                      <ImagesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/receituarios"
                  element={
                    <ProtectedRoute permission="documents.view">
                      <PrescriptionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/contratos"
                  element={
                    <ProtectedRoute permission="contracts.view">
                      <Layout>
                        <ContractsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/conformidade"
                  element={
                    <ProtectedRoute permission="compliance.view">
                      <CompliancePage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/ajuda/tutoriais/whatsapp-business" element={<WhatsAppBusinessTutorialPage />} />

                {/* --- Catch-all: manda para a home --- */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </ToastProvider>
        </OnboardingProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
